import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { TabConfig } from '../types';

export default function useQuestionnaireFooterNavigation(
  tabConfigs: ReadonlyArray<TabConfig>,
  basePath: string,
  terminalTabId: string,
) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const activeTabIndex = useMemo(() => {
    const activePath = pathname.replace(`${basePath}/`, '').split('/')[0];
    const matchingTabIndex = tabConfigs.findIndex((tabConfig) => tabConfig.path === activePath);

    return matchingTabIndex === -1 ? 0 : matchingTabIndex;
  }, [basePath, pathname, tabConfigs]);

  const isFirstPage = activeTabIndex === 0;
  const isTerminalPage = tabConfigs[activeTabIndex].id === terminalTabId;

  const navigateToTab = useCallback(
    (tabIndex: number) => {
      navigate(`${basePath}/${tabConfigs[tabIndex].path}`);
    },
    [basePath, navigate, tabConfigs],
  );

  const handlePrevious = useCallback(() => {
    if (isFirstPage) {
      return;
    }

    navigateToTab(activeTabIndex - 1);
  }, [activeTabIndex, isFirstPage, navigateToTab]);

  const handleNext = useCallback(() => {
    if (isTerminalPage) {
      return;
    }

    navigateToTab(activeTabIndex + 1);
  }, [activeTabIndex, isTerminalPage, navigateToTab]);

  return { handleNext, handlePrevious, isFirstPage, isTerminalPage };
}
